import React, { useRef, useState } from 'react';
import { Building2, Check, Loader2, Snowflake, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { companyRegistrationApi, paymentsApi, uploadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const DEFAULT_PLATFORM_FEE_RATE = 0.025;
const DEFAULT_GST_RATE = 0.18;
const RAZORPAY_MAX_MINOR_UNITS = {
  USD: 500000,
};
const RECIPIENT_BANK = {
  accountName: '1 GLOBE ALLIANCE PRIVATE LIMITED',
  bankName: 'ICICI BANK',
  usd: { accountNumber: '195606000031', swiftCode: 'ICICINBBCTS' },
  inr: { accountNumber: '195605004266', ifscCode: 'ICIC00001956' },
};
const RAZORPAY_ATTEMPT_STORAGE_KEY = 'upgrade_razorpay_attempt_payment_id';

const formatMoney = (value) => {
  return `$ ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getTodayDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PaymentGateway = ({
  paymentId,
  amount,
  tierAnnualPrice,
  pricingBreakdown,
  description,
  productId,
  gstDetails,
  onSuccess,
  onCancel
}) => {
  const { member, user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentGateway, setPaymentGateway] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successTitle, setSuccessTitle] = useState('Success');
  const [successMessage, setSuccessMessage] = useState('');
  const [successContext, setSuccessContext] = useState(null);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [gstNumber, setGstNumber] = useState(gstDetails?.gst_number || '');
  const [gstCompanyLegalName, setGstCompanyLegalName] = useState(gstDetails?.company_legal_name || '');
  const [gstUserName, setGstUserName] = useState(gstDetails?.user_name || '');
  const [payerAccountName, setPayerAccountName] = useState('');
  const [payerBankName, setPayerBankName] = useState('');
  const [payerAccountLast4, setPayerAccountLast4] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofFileName, setProofFileName] = useState('');
  const [hasInitiatedRazorpayPayment, setHasInitiatedRazorpayPayment] = useState(false);
  const bankProofInputRef = useRef(null);
  const wasPageHiddenRef = useRef(false);

  const isRegisteredUser = (member?.tier || '').toLowerCase() === 'registered user';
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const shouldBlockPayment = isRegisteredUser && !isCompanyApproved;
  const buyerCountry = (
    pricingBreakdown?.buyer_country
    || member?.country
    || companyRegistration?.company_info?.country
    || companyRegistration?.country
    || ''
  ).trim();
  const isIndiaBuyer = typeof pricingBreakdown?.is_india_buyer === 'boolean'
    ? pricingBreakdown.is_india_buyer
    : ['india', 'in', 'bharat'].includes(buyerCountry.toLowerCase());
  const fallbackSubscriptionAmount = toNumber(tierAnnualPrice) > 0
    ? toNumber(tierAnnualPrice)
    : toNumber(amount);
  const subscriptionAmount = pricingBreakdown?.subscription_amount != null
    ? toNumber(pricingBreakdown.subscription_amount)
    : fallbackSubscriptionAmount;
  const platformFeeRate = pricingBreakdown?.platform_fee_rate != null
    ? toNumber(pricingBreakdown.platform_fee_rate)
    : DEFAULT_PLATFORM_FEE_RATE;
  const platformFeeAmount = pricingBreakdown?.platform_fee != null
    ? toNumber(pricingBreakdown.platform_fee)
    : (subscriptionAmount * platformFeeRate);
  const gstRate = pricingBreakdown?.gst_rate != null
    ? toNumber(pricingBreakdown.gst_rate)
    : DEFAULT_GST_RATE;
  const gstAmount = pricingBreakdown?.gst_amount != null
    ? toNumber(pricingBreakdown.gst_amount)
    : (isIndiaBuyer ? ((subscriptionAmount + platformFeeAmount) * gstRate) : 0);
  const totalAmountDue = pricingBreakdown?.total_amount != null
    ? toNumber(pricingBreakdown.total_amount)
    : (subscriptionAmount + platformFeeAmount + gstAmount);
  const gstRateLabel = `${(gstRate * 100).toFixed(0)}%`;
  const platformFeeRateLabel = `${(platformFeeRate * 100).toFixed(1)}%`;
  const paymentCurrency = 'USD';
  const razorpayMaxMinorUnits = RAZORPAY_MAX_MINOR_UNITS[paymentCurrency];
  const razorpayUnavailableForAmount = Boolean(
    razorpayMaxMinorUnits && Math.round(totalAmountDue * 100) > razorpayMaxMinorUnits
  );
  const razorpayLimitMessage = razorpayMaxMinorUnits
    ? `Razorpay supports up to ${paymentCurrency} ${(razorpayMaxMinorUnits / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} per payment for this flow. Please use Bank Transfer for larger amounts.`
    : '';

  React.useEffect(() => {
    // Don't overwrite if GST details were already pre-filled from an existing payment
    if (!gstDetails?.company_legal_name) {
      setGstCompanyLegalName(
        member?.company_name
        || companyRegistration?.company_info?.legal_name
        || companyRegistration?.company_name
        || ''
      );
    }
    if (!gstDetails?.user_name) {
      setGstUserName(
        member?.individual_name
        || user?.full_name
        || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
        || ''
      );
    }
  }, [companyRegistration, member?.company_name, member?.individual_name, user?.first_name, user?.full_name, user?.last_name, gstDetails]);

  React.useEffect(() => {
    let cancelled = false;

    if (!isRegisteredUser) return undefined;

    companyRegistrationApi.getMyRegistration()
      .then((res) => {
        if (!cancelled && res.data?.exists) {
          setCompanyRegistration(res.data);
        }
      })
      .catch((error) => {
        if (!cancelled && error.response?.status !== 404) {
          console.error('Failed to load company registration:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRegisteredUser]);

  React.useEffect(() => {
    if (paymentMethod === 'razorpay' && razorpayUnavailableForAmount) {
      setPaymentMethod('bank');
    }
  }, [paymentMethod, razorpayUnavailableForAmount]);

  const refreshPaymentStatus = React.useCallback(async () => {
    if (!paymentId) return;
    try {
      const res = await paymentsApi.getPayment(paymentId);
      setPaymentStatus(String(res.data?.status || ''));
      setPaymentGateway(String(res.data?.gateway || ''));
    } catch {
      setPaymentStatus('');
      setPaymentGateway('');
    }
  }, [paymentId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!paymentId) return undefined;

    const fetchInitialPaymentStatus = async () => {
      try {
        const res = await paymentsApi.getPayment(paymentId);
        if (cancelled) return;
        setPaymentStatus(String(res.data?.status || ''));
        setPaymentGateway(String(res.data?.gateway || ''));
      } catch {
        if (cancelled) return;
        setPaymentStatus('');
        setPaymentGateway('');
      }
    };

    fetchInitialPaymentStatus();
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  React.useEffect(() => {
    if (!paymentId || !hasInitiatedRazorpayPayment) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasPageHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === 'visible' && wasPageHiddenRef.current) {
        wasPageHiddenRef.current = false;
        refreshPaymentStatus();
      }
    };

    const handleWindowFocus = () => {
      if (wasPageHiddenRef.current) {
        wasPageHiddenRef.current = false;
        refreshPaymentStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [hasInitiatedRazorpayPayment, paymentId, refreshPaymentStatus]);

  React.useEffect(() => {
    if (!paymentId) return;
    try {
      const storedPaymentId = window.localStorage.getItem(RAZORPAY_ATTEMPT_STORAGE_KEY);
      if (storedPaymentId === paymentId) {
        setHasInitiatedRazorpayPayment(true);
      }
    } catch {
      // Ignore localStorage access issues
    }
  }, [paymentId]);

  React.useEffect(() => {
    if (!paymentId || !hasInitiatedRazorpayPayment) return undefined;

    const intervalId = window.setInterval(() => {
      refreshPaymentStatus();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasInitiatedRazorpayPayment, paymentId, refreshPaymentStatus]);

  React.useEffect(() => {
    const normalizedStatus = paymentStatus.toLowerCase();
    const isTerminal = ['failed', 'completed', 'success', 'paid', 'cancelled', 'expired'].includes(normalizedStatus);
    if (isTerminal) {
      setHasInitiatedRazorpayPayment(false);
      try {
        const storedPaymentId = window.localStorage.getItem(RAZORPAY_ATTEMPT_STORAGE_KEY);
        const shouldClearAttemptMarker = ['completed', 'success', 'paid'].includes(normalizedStatus);
        if (storedPaymentId === paymentId && shouldClearAttemptMarker) {
          window.localStorage.removeItem(RAZORPAY_ATTEMPT_STORAGE_KEY);
        }
      } catch {
        // Ignore localStorage access issues
      }
    }
  }, [paymentId, paymentStatus]);

  React.useEffect(() => {
    setHasInitiatedRazorpayPayment(false);
    wasPageHiddenRef.current = false;
    try {
      const storedPaymentId = window.localStorage.getItem(RAZORPAY_ATTEMPT_STORAGE_KEY);
      if (storedPaymentId === paymentId) {
        setHasInitiatedRazorpayPayment(true);
      }
    } catch {
      // Ignore localStorage access issues
    }
  }, [paymentId]);

  const isRazorpayFailed = (
    hasInitiatedRazorpayPayment === false
    && paymentStatus.toLowerCase() === 'failed'
    && ['razorpay', 'payment_link', ''].includes(paymentGateway.toLowerCase())
  );

  const isGstFormValid = () => {
    if (!isIndiaBuyer) return true;
    return Boolean(
      gstNumber.trim()
      && gstCompanyLegalName.trim()
      && gstUserName.trim()
    );
  };

  const gstDetailsPayload = isIndiaBuyer ? {
    gst_number: gstNumber.trim(),
    company_legal_name: gstCompanyLegalName.trim(),
    user_name: gstUserName.trim(),
  } : null;

  const renderGstDetailsSection = () => {
    if (!isIndiaBuyer) return null;

    return (
      <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div>
          <p className="text-sm font-semibold text-amber-900">GST Details</p>
          <p className="text-xs text-amber-700 mt-1">These details will be used on your tax invoice.</p>
        </div>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="gstNumber">GST Number</Label>
            <Input
              id="gstNumber"
              placeholder="Enter GST number"
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gstCompanyLegalName">Company Legal Name</Label>
            <Input
              id="gstCompanyLegalName"
              placeholder="Company legal name"
              value={gstCompanyLegalName}
              onChange={(e) => setGstCompanyLegalName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gstUserName">Name of User</Label>
            <Input
              id="gstUserName"
              placeholder="Name of user"
              value={gstUserName}
              onChange={(e) => setGstUserName(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  };

  const handlePaymentLink = async () => {
    if (processingRef.current) return;
    if (shouldBlockPayment) {
      toast.error('Complete company registration and wait for approval before making payment.');
      return;
    }
    if (!paymentId || !productId) {
      toast.error('Payment record not found. Please try again.');
      return;
    }
    if (!isGstFormValid()) {
      toast.error('Please complete the GST details.');
      return;
    }
    if (razorpayUnavailableForAmount) {
      toast.error(razorpayLimitMessage);
      return;
    }

    processingRef.current = true;
    setProcessing(true);
    try {
      const customerName = (
        gstUserName.trim()
        || member?.individual_name
        || user?.full_name
        || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
        || member?.company_name
        || 'Member'
      );
      const payload = {
        payment_id: paymentId,
        member_id: member?.member_id,
        plan_id: productId,
        plan_name: description,
        amount: Math.round(totalAmountDue * 100),
        currency: paymentCurrency,
        ...(gstDetailsPayload || {}),
        customer: {
          name: customerName,
          email: user?.email || member?.email,
          contact: user?.phone || member?.phone || '',
        },
      };

      const res = await paymentsApi.createPaymentLink(payload);
      const shortUrl = res.data?.short_url;

      if (!shortUrl) {
        toast.error('Payment link could not be created. Please try again.');
        return;
      }

      const newWindow = window.open(shortUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        toast.error('Popup blocked. Please allow popups and click Pay again to open payment in a new tab.');
        return;
      }
      setHasInitiatedRazorpayPayment(true);
      try {
        window.localStorage.setItem(RAZORPAY_ATTEMPT_STORAGE_KEY, paymentId);
      } catch {
        // Ignore localStorage access issues
      }
      refreshPaymentStatus();

      setSuccessTitle('Payment Link Created');
      setSuccessMessage('Payment link opened. Complete the payment in Razorpay to finish your upgrade.');
      setSuccessContext({ method: 'razorpay' });
      setShowSuccessDialog(true);
      toast.success('Payment link created.');
    } catch (err) {
      toast.error(getErrorMessage(err.response?.data?.detail, 'Failed to create payment link'));
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  const isBankFormValid = () => {
    const last4 = payerAccountLast4.replace(/\D/g, '');
    return (
      payerAccountName.trim()
      && payerBankName.trim()
      && last4.length === 4
      && transactionReference.trim()
      && transferDate
      && proofFile
      && isGstFormValid()
    );
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload PDF, JPG, or PNG only.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB.');
      return;
    }

    setProofFile(file);
    setProofFileName(file.name);
  };

  const handleBankTransferSubmit = async () => {
    const todayDate = getTodayDateValue();

    if (processingRef.current) return;
    if (shouldBlockPayment) {
      toast.error('Complete company registration and wait for approval before making payment.');
      return;
    }
    if (!paymentId) {
      toast.error('Payment record not found. Please try again.');
      return;
    }
    if (!isBankFormValid()) {
      toast.error('Please fill all fields and upload proof of transfer.');
      return;
    }
    if (transferDate > todayDate) {
      toast.error('Transfer date cannot be in the future.');
      return;
    }

    processingRef.current = true;
    setProcessing(true);
    try {
      const uploadRes = await uploadsApi.uploadFile(
        proofFile,
        'payment_proof',
        paymentId,
        member?.member_id
      );
      const base = process.env.REACT_APP_BACKEND_URL || '';
      let proofUrl = uploadRes.data?.file_url || uploadRes.data?.url || uploadRes.data?.file?.url;
      if (proofUrl && proofUrl.startsWith('/')) {
        proofUrl = `${base}${proofUrl}`;
      }

      await paymentsApi.submitBankTransferDetails(paymentId, {
        payer_account_name: payerAccountName.trim(),
        payer_bank_name: payerBankName.trim(),
        payer_account_last4: payerAccountLast4.replace(/\D/g, '').slice(-4),
        transaction_reference: transactionReference.trim(),
        transfer_date: transferDate,
        proof_url: proofUrl,
        gst_details: gstDetailsPayload,
      });

      setSuccessTitle('Transfer Details Submitted');
      setSuccessMessage('Transfer details submitted successfully. Your payment will be verified within 24 hours.');
      setSuccessContext({ method: 'bank_transfer' });
      setShowSuccessDialog(true);
      toast.success('Details submitted. Payment will be verified within 24 hours.');
    } catch (err) {
      toast.error(getErrorMessage(err.response?.data?.detail, 'Failed to submit transfer details'));
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    onSuccess?.(successContext);
  };

  return (
    <div className="space-y-6">
      {shouldBlockPayment && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            Complete company registration and wait for approval before making any membership payment.
            Registered users remain on the Free tier until approval.
          </CardContent>
        </Card>
      )}

      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-blue-900 font-medium">Amount Due</p>
              <p className="text-sm text-blue-600 mt-1">{description}</p>
            </div>
            <div className="space-y-2 text-sm text-blue-900">
              <div className="flex items-center justify-between">
                <span>Subscription Amount</span>
                <span className="font-medium">{formatMoney(subscriptionAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Platform Fee ({platformFeeRateLabel})</span>
                <span className="font-medium">{formatMoney(platformFeeAmount)}</span>
              </div>
              {isIndiaBuyer && (
                <div className="flex items-center justify-between">
                  <span>GST ({gstRateLabel})</span>
                  <span className="font-medium">{formatMoney(gstAmount)}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-blue-200 pt-4">
              <p className="text-base font-semibold text-blue-900">Total</p>
              <p className="text-3xl font-bold text-blue-700">{formatMoney(totalAmountDue)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="razorpay" disabled={shouldBlockPayment || razorpayUnavailableForAmount}>
            <Snowflake size={16} className="mr-2 text-blue-600" />
            Razorpay
          </TabsTrigger>
          <TabsTrigger
            value="bank"
            disabled={shouldBlockPayment}
            className="border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:text-blue-900 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
          >
            <Building2 size={16} className="mr-2" />
            Bank Transfer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="razorpay">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pay With Razorpay</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 p-6">
              {razorpayUnavailableForAmount ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  {razorpayLimitMessage}
                </div>
              ) : isRazorpayFailed ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  Razorpay payment failed. You can try again or proceed with bank transfer.
                </div>
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  A secure Razorpay payment link will open in a new tab. Complete the payment there to finish your upgrade.
                </div>
              )}

              {renderGstDetailsSection()}

              <div className="flex justify-end gap-3 pt-2">
                {isRazorpayFailed && (
                  <Button
                    type="button"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => setPaymentMethod('bank')}
                    disabled={processing || shouldBlockPayment}
                  >
                    <Building2 size={16} className="mr-2" />
                    Bank Transfer
                  </Button>
                )}
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handlePaymentLink}
                  disabled={processing || !isGstFormValid() || shouldBlockPayment || razorpayUnavailableForAmount}
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatMoney(totalAmountDue)}`
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Building2 size={20} className="mr-2" />
                Bank Transfer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="font-semibold">Transfer To</Label>
                <div className="rounded-lg border bg-muted/40 p-4 space-y-1.5 text-sm">
                  <p><span className="text-muted-foreground">Account Name:</span> {RECIPIENT_BANK.accountName}</p>
                  <p><span className="text-muted-foreground">Bank Name:</span> {RECIPIENT_BANK.bankName}</p>
                  <p className="pt-2 font-medium">USD (SWIFT)</p>
                  <p><span className="text-muted-foreground">Account No.:</span> {RECIPIENT_BANK.usd.accountNumber}</p>
                  <p><span className="text-muted-foreground">SWIFT Code:</span> {RECIPIENT_BANK.usd.swiftCode}</p>
                  <p className="pt-2 font-medium">INR (IFSC)</p>
                  <p><span className="text-muted-foreground">Account No.:</span> {RECIPIENT_BANK.inr.accountNumber}</p>
                  <p><span className="text-muted-foreground">IFSC Code:</span> {RECIPIENT_BANK.inr.ifscCode}</p>
                  <p className="font-medium text-blue-700 mt-2">Amount: {formatMoney(totalAmountDue)}</p>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Complete the bank transfer and submit the details below. Your payment will be verified within 24 hours.
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payerAccountName">Your Account Name</Label>
                  <Input
                    id="payerAccountName"
                    placeholder="Name on your bank account"
                    value={payerAccountName}
                    onChange={(e) => setPayerAccountName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payerBankName">Your Bank Name</Label>
                  <Input
                    id="payerBankName"
                    placeholder="Name of your bank"
                    value={payerBankName}
                    onChange={(e) => setPayerBankName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payerAccountLast4">Your Account Number (last 4 digits)</Label>
                  <Input
                    id="payerAccountLast4"
                    placeholder="1234"
                    maxLength={4}
                    value={payerAccountLast4}
                    onChange={(e) => setPayerAccountLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transactionReference">Transaction Reference Number</Label>
                  <Input
                    id="transactionReference"
                    placeholder="e.g. TRF2025012345"
                    value={transactionReference}
                    onChange={(e) => setTransactionReference(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transferDate">Transfer Date</Label>
                  <Input
                    id="transferDate"
                    type="date"
                    value={transferDate}
                    max={getTodayDateValue()}
                    onChange={(e) => setTransferDate(e.target.value > getTodayDateValue() ? getTodayDateValue() : e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Upload Proof of Transfer</Label>
                  <div
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30 p-6 transition-colors hover:bg-muted/50"
                    onClick={() => bankProofInputRef.current?.click()}
                  >
                    <input
                      ref={bankProofInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleProofFileChange}
                    />
                    <Upload size={24} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {proofFileName || 'Click to upload (PDF, JPG, PNG)'}
                    </span>
                    <p className="text-xs text-muted-foreground">Upload a screenshot or PDF of your bank transfer receipt</p>
                  </div>
                </div>
              </div>

              {renderGstDetailsSection()}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleBankTransferSubmit}
                  disabled={processing || !isBankFormValid() || shouldBlockPayment}
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Building2 size={16} className="mr-2" />
                      Submit Transfer Details
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showSuccessDialog} onOpenChange={(open) => !open && handleSuccessClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <Check size={24} className="mr-2" />
              {successTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-700">{successMessage}</p>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSuccessClose}>
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentGateway;
